import { Box, useTheme } from '@mui/material';
import { clamp } from 'lodash';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { US_LETTER_SCALE_WIDTH_PX } from '@/modules/presentation/PresentationSlide/PresentationSlide.constants';
import { SlideHoverHaloRefs } from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import { SLIDE_CONTENT_TOP_OFFSET_SPACING } from '@/modules/slideEditor/shared/slideContentSpacing';
import { getSlideTextHorizontalPosition } from '@/modules/slideEditor/shared/slideTextPosition';
import { COLORS } from '@/styles/tokens/colors';

import { useSlideOriginDrag } from './hooks/useSlideOriginDrag';
import {
  clampTextOriginX,
  DragOriginBounds,
  getDragOriginBounds,
  ScreenEdges,
} from './SlideConfigForm.constants';
import {
  CoverSlideConfigBodyTextAlignment,
  SlideConfigFormShape,
  TEXT_WIDTH_PX_MAX,
  TEXT_WIDTH_PX_MIN,
} from './SlideConfigForm.types';

const SET_VALUE_OPTIONS = { shouldDirty: true } as const;

const HALO_OUTLINE = `1px solid ${COLORS.BLUE[600]}`;
const HALO_OFFSET = '3px';

/** Browser-native tooltip shown on the drag overlays. */
const DRAG_TOOLTIP = 'Click and drag to move';

/** Browser-native tooltip shown on the text width resize handle. */
const RESIZE_TOOLTIP = 'Click and drag to resize the text box';

/**
 * Width of the text resize handle's grab zone, in slide-coordinate
 * pixels. The preview renders the slide scaled down, so this is wider
 * than the handle appears on screen.
 */
const RESIZE_HANDLE_WIDTH_PX = 24;

/**
 * Imperatively apply / clear the hover halo on a slide internal
 * element. Used by an effect that watches the corresponding drag
 * overlay's hover state. Returns a cleanup that always clears the
 * styles, so toggling hover or unmounting the overlay leaves the
 * element back at its baseline.
 */
function applyHalo(el: HTMLElement | null, on: boolean): void {
  if (!el) return;
  el.style.outline = on ? HALO_OUTLINE : '';
  el.style.outlineOffset = on ? HALO_OFFSET : '';
}

/** Screen pixels per slide-coordinate pixel for a slide content area. */
function getAreaScale(area: DOMRect): number {
  return area.width / US_LETTER_SCALE_WIDTH_PX;
}

/**
 * Limits for the text drag. The block (with the inline logo, when it
 * shows) stays inside the content area, and the first title line stays
 * above the bottom of the content area. The X limit for the block
 * itself comes from `clampTextOriginX`; here X is limited only so the
 * inline logo stays on the slide.
 */
function getTextDragBounds(
  block: HTMLElement | null,
  originX: number,
  originY: number
): DragOriginBounds | null {
  const areaElement = block?.closest(
    '[data-name$=".TitleBlock"]'
  )?.parentElement;
  if (!block || !areaElement) return null;
  const area = areaElement.getBoundingClientRect();
  const scale = getAreaScale(area);
  if (scale <= 0) return null;

  const blockRect = block.getBoundingClientRect();
  // The title renders as the block's only h1 (the `h1_display` variant).
  const title = block.querySelector('h1');
  let visibleBottom = blockRect.bottom;
  if (title) {
    const titleRect = title.getBoundingClientRect();
    // The computed line height is in unscaled slide pixels.
    const lineHeight = parseFloat(getComputedStyle(title).lineHeight);
    const firstLineHeight = Number.isNaN(lineHeight)
      ? titleRect.height
      : Math.min(titleRect.height, lineHeight * scale);
    visibleBottom = titleRect.top + firstLineHeight;
  }
  // The title block also holds the hidden logo slot that keeps the title
  // in place when the logo is not anchored. Keep that slot on the slide
  // too, so the logo is not cut off if the admin anchors it again. A
  // scaled inline logo can extend above the title block instead.
  const titleBlock = areaElement.querySelector('[data-name$=".TitleBlock"]');
  const titleBlockTop =
    titleBlock?.getBoundingClientRect().top ?? blockRect.top;
  const yBounds = getDragOriginBounds({
    originX,
    originY,
    element: {
      top: Math.min(titleBlockTop, blockRect.top),
      bottom: blockRect.bottom,
      left: blockRect.left,
      right: blockRect.right,
    },
    visibleBottom,
    area,
    scale,
  });

  // The title block holds the inline logo, or the hidden logo slot when
  // the logo is not anchored. Keep either one on the slide. A logo can be
  // more than one image (the fallback dark logo is an icon and a
  // wordmark), so measure all of them together. Images in the editable
  // title and body text are not part of the logo.
  const logoRect = getImagesEdges(
    Array.from((titleBlock ?? block).querySelectorAll('img')).filter(
      (img) => !img.closest('[contenteditable]')
    )
  );
  if (!logoRect) {
    return { ...yBounds, minX: -Infinity, maxX: Infinity };
  }
  const xBounds = getDragOriginBounds({
    originX,
    originY,
    element: logoRect,
    visibleBottom: logoRect.bottom,
    area,
    scale,
  });
  return { ...yBounds, minX: xBounds.minX, maxX: xBounds.maxX };
}

/**
 * Combined screen edges of the given images, or null when there are
 * none. A logo can be more than one image: the fallback dark logo is an
 * icon and a separate wordmark.
 */
function getImagesEdges(images: HTMLImageElement[]): ScreenEdges | null {
  if (images.length === 0) return null;
  const rects = images.map((img) => img.getBoundingClientRect());
  return {
    top: Math.min(...rects.map((r) => r.top)),
    bottom: Math.max(...rects.map((r) => r.bottom)),
    left: Math.min(...rects.map((r) => r.left)),
    right: Math.max(...rects.map((r) => r.right)),
  };
}

/** Limits for a custom-positioned logo: the whole logo stays inside. */
function getLogoDragBounds(
  logo: HTMLElement | null,
  originX: number,
  originY: number
): DragOriginBounds | null {
  const areaElement = logo?.parentElement;
  if (!logo || !areaElement) return null;
  const area = areaElement.getBoundingClientRect();
  const scale = getAreaScale(area);
  if (scale <= 0) return null;
  // The image can be taller than its wrapper (the custom logo has no
  // height cap), so measure the images. Use the wrapper if none render.
  const logoRect =
    getImagesEdges(Array.from(logo.querySelectorAll('img'))) ??
    logo.getBoundingClientRect();
  return getDragOriginBounds({
    originX,
    originY,
    element: logoRect,
    visibleBottom: logoRect.bottom,
    area,
    scale,
  });
}

interface SlideOriginDragOverlayProps {
  /** Ref to the rendered slide root, used by the drag hooks for scale. */
  slideRef: RefObject<HTMLElement | null>;
  /**
   * Refs to slide-internal hover targets (title block, inline logo
   * wrapper, custom-positioned logo). The overlay imperatively
   * outlines the matching ref when the user mouses over the
   * corresponding drag layer. The slide components attach these refs
   * to the relevant elements; unmounted elements have `current === null`
   * and are skipped.
   */
  hoverHaloRefs?: SlideHoverHaloRefs;
  /**
   * When true (e.g. inheriting divider), the drag layers don't render.
   * No hidden origin form value can change while the visual is locked
   * to the cover's design. The caller is responsible for preventing
   * text-editor interaction in the preview (typically by leaving the
   * slide wrapper at `pointerEvents: 'none'` when this prop is true).
   */
  disabled?: boolean;
}

/**
 * Three stacked drag zones — background, text, and (optionally) logo —
 * that drive the corresponding origin form fields when the admin drags
 * inside the slide preview. Also drives the hover halo on the rendered
 * title-block, inline logo, and custom-positioned logo via the supplied
 * `hoverHaloRefs`.
 */
export function SlideOriginDragOverlay({
  slideRef,
  hoverHaloRefs,
  disabled = false,
}: SlideOriginDragOverlayProps) {
  const theme = useTheme();
  const { control, setValue } = useFormContext<SlideConfigFormShape>();

  // Single `useWatch` subscription is cheaper than individual
  // `watch(...)` calls — `watch` from `useFormContext` re-renders on
  // every form change, whereas `useWatch` re-renders only when the
  // listed fields change.
  const [
    backgroundImageUrl,
    backgroundImageOriginXValue,
    backgroundImageOriginYValue,
    textOriginXValue,
    textOriginYValue,
    textWidthValue,
    bodyTextAlignment,
    useDefaultLogoPosition,
    showLogo,
    logoOriginXValue,
    logoOriginYValue,
  ] = useWatch({
    control,
    name: [
      'backgroundImageUrl',
      'backgroundImageOriginX',
      'backgroundImageOriginY',
      'textOriginX',
      'textOriginY',
      'textWidth',
      'bodyTextAlignment',
      'useDefaultLogoPosition',
      'showLogo',
      'logoOriginX',
      'logoOriginY',
    ],
  });

  const backgroundOriginX = backgroundImageOriginXValue ?? 0;
  const backgroundOriginY = backgroundImageOriginYValue ?? 0;
  const textOriginX = textOriginXValue ?? 0;
  const textOriginY = textOriginYValue ?? 0;
  const logoOriginX = logoOriginXValue ?? 0;
  const logoOriginY = logoOriginYValue ?? 0;
  const textWidth = textWidthValue === '' ? null : Number(textWidthValue);
  const textHorizontalPosition = getSlideTextHorizontalPosition(
    theme.spacing(10.5),
    textOriginX,
    bodyTextAlignment,
    textWidth
  );

  // Right-aligned text keeps its right edge fixed, so its resize handle
  // sits on the left edge and dragging left makes the block wider.
  const isRightAligned =
    bodyTextAlignment === CoverSlideConfigBodyTextAlignment.RightAligned;
  // An unset width stretches to the opposite padding. Left-anchored
  // blocks lose the X offset from that full width; right-aligned blocks
  // move both edges, so they keep the full width.
  const renderedTextWidth =
    textWidth ?? TEXT_WIDTH_PX_MAX - (isRightAligned ? 0 : textOriginX);
  const resizeDirection = isRightAligned ? -1 : 1;

  const onBackgroundChange = useCallback(
    (nextX: number, nextY: number) => {
      setValue('backgroundImageOriginX', nextX, SET_VALUE_OPTIONS);
      setValue('backgroundImageOriginY', nextY, SET_VALUE_OPTIONS);
    },
    [setValue]
  );

  const onTextChange = useCallback(
    (nextX: number, nextY: number) => {
      // Keep a dragged text block on the slide, so a long drag past a
      // slide edge cannot save an invisible position.
      // A box with no width keeps its right edge at the slide padding,
      // so moving it only makes it narrower. Store the default width on
      // the first move, so the whole box moves and stops at each edge.
      let dragWidth = textWidth;
      if (dragWidth == null) {
        dragWidth = TEXT_WIDTH_PX_MAX;
        setValue('textWidth', String(dragWidth), SET_VALUE_OPTIONS);
      }
      setValue(
        'textOriginX',
        clampTextOriginX(nextX, bodyTextAlignment, dragWidth),
        SET_VALUE_OPTIONS
      );
      setValue('textOriginY', nextY, SET_VALUE_OPTIONS);
    },
    [setValue, bodyTextAlignment, textWidth]
  );

  const onTextWidthChange = useCallback(
    (nextX: number) => {
      const nextWidth = Math.min(
        TEXT_WIDTH_PX_MAX,
        Math.max(TEXT_WIDTH_PX_MIN, nextX * resizeDirection)
      );
      setValue('textWidth', String(nextWidth), SET_VALUE_OPTIONS);
    },
    [setValue, resizeDirection]
  );

  const onLogoChange = useCallback(
    (nextX: number, nextY: number) => {
      setValue('logoOriginX', nextX, SET_VALUE_OPTIONS);
      setValue('logoOriginY', nextY, SET_VALUE_OPTIONS);
    },
    [setValue]
  );

  const backgroundDrag = useSlideOriginDrag({
    originX: backgroundOriginX,
    originY: backgroundOriginY,
    onChange: onBackgroundChange,
    slideRef,
    disabled,
  });

  // The drag limits come from the rendered layout, so they include the
  // footer, the inline logo, and the title line height.
  const getTextBounds = useCallback(
    (originX: number, originY: number) =>
      getTextDragBounds(
        hoverHaloRefs?.titleHaloTarget?.current ?? null,
        originX,
        originY
      ),
    [hoverHaloRefs]
  );

  const textDrag = useSlideOriginDrag({
    originX: textOriginX,
    originY: textOriginY,
    onChange: onTextChange,
    slideRef,
    disabled,
    getBounds: getTextBounds,
  });

  // Reuse the origin drag hook on one axis. The signed origin makes a
  // drag toward the free edge always grow the block.
  const textWidthDrag = useSlideOriginDrag({
    originX: renderedTextWidth * resizeDirection,
    originY: 0,
    onChange: onTextWidthChange,
    slideRef,
    disabled,
  });

  const getLogoBounds = useCallback(
    (originX: number, originY: number) =>
      getLogoDragBounds(
        hoverHaloRefs?.customLogo?.current ?? null,
        originX,
        originY
      ),
    [hoverHaloRefs]
  );

  const logoDrag = useSlideOriginDrag({
    originX: logoOriginX,
    originY: logoOriginY,
    onChange: onLogoChange,
    slideRef,
    disabled,
    getBounds: getLogoBounds,
  });

  const showBackgroundDrag = !disabled && Boolean(backgroundImageUrl);
  const showTextDrag = !disabled;
  // Only mount the logo drag layer when the admin has explicitly opted
  // out of the default position AND the logo is visible at all —
  // otherwise the logo lives inside the title block (and is moved by
  // the text drag layer) or doesn't render at all.
  const showLogoDrag =
    !disabled && useDefaultLogoPosition === false && showLogo !== false;

  // Shift a text block or custom logo back inside the slide when its
  // saved origin puts it within the edge margin or past an edge. This
  // changes only the admin form; presentations render the saved values.
  // The limits are the same as the drag limits. Returns false when the
  // slide is not laid out yet, so the caller can try again.
  const shiftIntoSlide = (): boolean => {
    if (disabled) return true;
    const textBounds = getTextBounds(textOriginX, textOriginY);
    if (!textBounds) return false;
    const nextTextX = clampTextOriginX(
      clamp(textOriginX, textBounds.minX, textBounds.maxX),
      bodyTextAlignment,
      textWidth
    );
    const nextTextY = clamp(textOriginY, textBounds.minY, textBounds.maxY);
    if (nextTextX !== textOriginX) {
      setValue('textOriginX', nextTextX, SET_VALUE_OPTIONS);
    }
    if (nextTextY !== textOriginY) {
      setValue('textOriginY', nextTextY, SET_VALUE_OPTIONS);
    }
    const logoBounds = showLogoDrag
      ? getLogoBounds(logoOriginX, logoOriginY)
      : null;
    if (logoBounds) {
      const nextX = clamp(logoOriginX, logoBounds.minX, logoBounds.maxX);
      const nextY = clamp(logoOriginY, logoBounds.minY, logoBounds.maxY);
      if (nextX !== logoOriginX) {
        setValue('logoOriginX', nextX, SET_VALUE_OPTIONS);
      }
      if (nextY !== logoOriginY) {
        setValue('logoOriginY', nextY, SET_VALUE_OPTIONS);
      }
    }
    return true;
  };
  const shiftIntoSlideRef = useRef(shiftIntoSlide);
  shiftIntoSlideRef.current = shiftIntoSlide;

  // Check after each render, because form changes (size, width,
  // alignment) can move the block. A value is set only when it changes,
  // so this settles after one extra render.
  //
  // The slide scales itself after it mounts, so the layout can be
  // unmeasurable on the first renders. Try again on later frames.
  useEffect(() => {
    let frame = 0;
    let attempts = 0;
    const attempt = () => {
      if (shiftIntoSlideRef.current() || attempts >= 60) return;
      attempts += 1;
      frame = requestAnimationFrame(attempt);
    };
    attempt();
    return () => cancelAnimationFrame(frame);
  });

  // Also check when the measured elements change size without a render:
  // the slide content area gets its size after the slide scales, and
  // the logo image or a web font can finish loading later.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => shiftIntoSlideRef.current());
    const titleTarget = hoverHaloRefs?.titleHaloTarget?.current;
    [
      titleTarget,
      titleTarget?.closest('[data-name$=".TitleBlock"]')?.parentElement,
      hoverHaloRefs?.customLogo?.current,
    ].forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [hoverHaloRefs, showLogoDrag]);

  // Track hover on each drag layer so the corresponding slide-internal
  // element can be outlined. We do this with `useIsElementHovered` and
  // imperative style application rather than CSS `:has()` selectors so
  // the hover wiring is an explicit React data flow — renaming a slide
  // element won't silently break the halo.
  //
  // The text drag overlay always outlines a single target
  // (`titleHaloTarget`). When the admin has the default-position
  // checkbox on, the slide pads that target upward to also cover the
  // scaled inline logo, so a single rectangle wraps both. When off,
  // the inline logo isn't part of the halo target — the custom logo
  // has its own outline (`customLogo`).
  //
  // Mount state is folded into the effective hover so the halo clears
  // when the overlay Box unmounts mid-hover (e.g., `disabled` flips
  // true or a sub-brand switch programmatically remounts the form):
  // the unmounting Box never fires `onMouseLeave`, so the hovered
  // state would otherwise stick at `true` and leave the outline
  // permanently applied.
  const [textHovered, textHoverProps] = useIsElementHovered();
  const [logoHovered, logoHoverProps] = useIsElementHovered();

  const effectiveTextHovered = textHovered && showTextDrag;
  const effectiveLogoHovered = logoHovered && showLogoDrag;

  useEffect(() => {
    const titleEl = hoverHaloRefs?.titleHaloTarget?.current ?? null;
    applyHalo(titleEl, effectiveTextHovered);
    return () => {
      applyHalo(titleEl, false);
    };
  }, [effectiveTextHovered, hoverHaloRefs]);

  useEffect(() => {
    const customEl = hoverHaloRefs?.customLogo?.current ?? null;
    applyHalo(customEl, effectiveLogoHovered);
    return () => {
      applyHalo(customEl, false);
    };
  }, [effectiveLogoHovered, hoverHaloRefs]);

  return (
    <>
      {showBackgroundDrag && (
        <Box
          data-name="SlideOriginDragOverlay.Background"
          title={DRAG_TOOLTIP}
          onPointerDown={backgroundDrag.onPointerDown}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            cursor: backgroundDrag.isDragging ? 'grabbing' : 'grab',
            // Transparent overlay: only the cursor + drag listeners
            // give it away.
            backgroundColor: 'transparent',
          }}
        />
      )}
      {showTextDrag && (
        <Box
          {...textHoverProps}
          data-name="SlideOriginDragOverlay.Text"
          title={DRAG_TOOLTIP}
          onPointerDown={(e) => {
            // Stop bubbling so the lower background layer doesn't also
            // start a drag at the same time.
            e.stopPropagation();
            textDrag.onPointerDown(e);
          }}
          sx={{
            position: 'absolute',
            // Track the rendered title block 1:1 so its drag target follows
            // the alignment-aware horizontal position.
            top: `calc(${theme.spacing(SLIDE_CONTENT_TOP_OFFSET_SPACING)} + ${textOriginY}px)`,
            ...textHorizontalPosition,
            // Generous fixed-height grab zone in slide-coordinate pixels.
            height: theme.spacing(60),
            zIndex: 2,
            cursor: textDrag.isDragging ? 'grabbing' : 'grab',
            backgroundColor: 'transparent',
          }}
        >
          <Box
            data-name="SlideOriginDragOverlay.TextWidthHandle"
            title={RESIZE_TOOLTIP}
            onPointerDown={(e) => {
              // Stop bubbling so the text move drag doesn't also start.
              e.stopPropagation();
              textWidthDrag.onPointerDown(e);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              [isRightAligned ? 'left' : 'right']: -RESIZE_HANDLE_WIDTH_PX / 2,
              width: RESIZE_HANDLE_WIDTH_PX,
              cursor: 'ew-resize',
              // Show a thin guide line in the handle center while the
              // text block is hovered or being resized.
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                borderLeft:
                  effectiveTextHovered || textWidthDrag.isDragging
                    ? HALO_OUTLINE
                    : 'none',
              },
            }}
          />
        </Box>
      )}
      {showLogoDrag && (
        <Box
          {...logoHoverProps}
          data-name="SlideOriginDragOverlay.Logo"
          title={DRAG_TOOLTIP}
          onPointerDown={(e) => {
            // Sit above the text drag layer so a click on the logo
            // moves it instead of the title block.
            e.stopPropagation();
            logoDrag.onPointerDown(e);
          }}
          sx={{
            position: 'absolute',
            top: theme.spacing(SLIDE_CONTENT_TOP_OFFSET_SPACING),
            left: theme.spacing(10.5),
            transform: `translate(${logoOriginX}px, ${logoOriginY}px)`,
            // Track the Logomark.Wide max bounds so the grab area
            // matches the rendered logo.
            width: 640,
            height: 160,
            zIndex: 3,
            cursor: logoDrag.isDragging ? 'grabbing' : 'grab',
            backgroundColor: 'transparent',
          }}
        />
      )}
    </>
  );
}
